// flow/components/FlowCanvas.tsx
import { useMemo, useEffect, forwardRef } from 'react'
import {
  ReactFlow,
  Background,
  SelectionMode,
  BackgroundVariant,
  type Node,
  type Edge,
  ConnectionMode,
  MarkerType,
} from '@xyflow/react'
import Cursors from '@/components/Flow/Cursors/Cursors'
import FlowControls from '@/components/Flow/FlowControls'
import FlowMiniMap from '@/components/Flow/FlowMiniMap'
import FlowFullMap from '@/components/Flow/FlowFullMap'
import AnnotationEdge from '@/components/reactflow/edges/AnnotationEdge'
import { FloatingAnimatedEdge } from '@/components/edges/FloatingAnimatedEdge'
import { FloatingEdge } from '@/components/edges/FloatingEdge'
import { FloatingConnectionLine } from '@/components/edges/FloatingConnectionLine'
import { nodeTypes as getNodeTypes } from '@/utils/nodeTypes'
import '@xyflow/react/dist/style.css'
import '@/styles/flow.css'

interface FlowCanvasProps {
  // Core data
  nodes: Node[]
  edges: Edge[]
  visibleNodes: Node[]
  validEdges: Edge[]
  user: any
    // ReactFlow utilities
  initializeViewport: (instance: any) => void
  viewportSettings: any
  setViewportSettings: (viewport: any) => void
  
  // Interaction handlers
  panOnScroll: boolean
  setPanOnScroll: (pan: boolean) => void
  handleNodesChange: (changes: any) => void
  onEdgesChange: (changes: any) => void
  onNodeDragStart: (event: any, node: Node) => void
  onNodeDragStop: (event: any, node: Node) => void
  onNodeClick: (event: any, node: Node) => void
  selectNode: (nodeId: string | null) => void
  onNodeContextMenuWithCopy: (event: any, node: Node) => void
  onPaneContextMenuWithPaste: (event: any) => void
  onMove: (event: any, viewport: any) => void
  onPaneClick: (event: any) => void
  handlePaneOnClickAndCleanupActiveUsers: (event: any) => void
  
  // Mouse and cursor
  cursors: any
  onMouseMove: (event: any) => void
    // File handling
  handleFileDrop: (event: React.DragEvent<HTMLDivElement>, dropPosition?: { x: number; y: number }) => Promise<void>
  onDragOver: (event: React.DragEvent<HTMLDivElement>) => void
  onDrop: (event: React.DragEvent<HTMLDivElement>) => void
  
  // Edge handlers
  edgeHandlers: any
  
  // Theme and visual
  theme: 'light' | 'dark'
  currentBackgroundPreset: any
  
  // Map and controls
  zoomLevel: number
  showMap: boolean
  setShowNodeIndex: (show: boolean) => void
  handleNodeSelect: (nodeId: string) => void
  projectName?: string
  
  // Node creation functions
  newNode: any
  setShowMap: (show: boolean) => void
  onCreatingAnnotationNode: any
  createCustomNode: () => void
  createQuickStartNode: () => void
  onCreatingRandomSequenceNode: any
  createDocumentNode: () => void
  createChatNode: () => void
  createCompareFilesNode: () => void
  createSmartNotesNode: () => void
  createDocumentDesignerNode: () => void
  createNodeIntellica: () => void
  createNodeDiagrams: () => void
  createResearcherNode: () => void
  createFileMinerNode: () => void
  createJavascriptNode: () => void
  createImageGeneratorNode: () => void
  createNodeResource: () => void
  applyAutoLayout: () => void
  applyHorizontalLayout: () => void
  applyVerticalLayout: () => void
  applyGridLayout: () => void
  applyOptimizedGridLayout: () => void
  setDebug: (debug: boolean) => void
  
  // Flow bounds
  setFlowBounds: (bounds: DOMRect | null) => void
}

const proOptions = { hideAttribution: true }
const defaultEdgeOptions = {
  style: { strokeWidth: 2, color: '#F00' },
  animated: true,
  markerEnd: {
    type: MarkerType.Arrow,
  },
}

// Define edge types outside the component
const edgeTypesDefinition = {
  annotationedge: AnnotationEdge,
  floatinganimated: FloatingAnimatedEdge,
  floating: FloatingEdge,
}

export const FlowCanvas = forwardRef<HTMLDivElement, FlowCanvasProps>(({
  nodes,
  edges,
  visibleNodes,
  validEdges,
  user,
  initializeViewport,
  viewportSettings,
  setViewportSettings,
  panOnScroll,
  setPanOnScroll,
  handleNodesChange,
  onEdgesChange,
  onNodeDragStart,
  onNodeDragStop,
  onNodeClick,
  selectNode,
  onNodeContextMenuWithCopy,
  onPaneContextMenuWithPaste,
  onMove,
  onPaneClick,
  handlePaneOnClickAndCleanupActiveUsers,
  cursors,
  onMouseMove,
  handleFileDrop,
  onDragOver,
  onDrop,
  edgeHandlers,
  theme,
  currentBackgroundPreset,
  zoomLevel,
  showMap,
  setShowNodeIndex,
  handleNodeSelect,
  projectName,
  newNode,
  setShowMap,
  onCreatingAnnotationNode,
  createCustomNode,
  createQuickStartNode,
  onCreatingRandomSequenceNode,
  createDocumentNode,
  createChatNode,
  createCompareFilesNode,
  createSmartNotesNode,
  createDocumentDesignerNode,
  createNodeIntellica,
  createNodeDiagrams,
  createResearcherNode,
  createFileMinerNode,
  createJavascriptNode,
  createImageGeneratorNode,
  createNodeResource,
  applyAutoLayout,
  applyHorizontalLayout,
  applyVerticalLayout,
  applyGridLayout,
  applyOptimizedGridLayout,
  setDebug,
  setFlowBounds,
}, ref) => {
  
  const nodeTypes = useMemo(() => getNodeTypes(), [])
  const edgeTypes = useMemo(() => edgeTypesDefinition, [])
  
  // Note: Search match data is now updated directly in the Yjs store
  // so visibleNodes already contains the search match information
  useEffect(() => {
    if (ref && 'current' in ref && ref.current) {
      setFlowBounds((ref.current as HTMLElement).getBoundingClientRect())
    }
  }, [setFlowBounds, ref])

  return (
    <div className="flex-grow min-h-0" style={{ height: 'calc(100vh)' }}>
      {/* Only handle file drops here, not node type drops */}
      <div
        className="h-full"
        onDrop={(event) => {
          // If files are being dropped, handle file upload
          if (
            event.dataTransfer?.files &&
            event.dataTransfer.files.length > 0
          ) {
            // Pass mouse position to handleFileDrop
            handleFileDrop(event, { x: event.clientX, y: event.clientY })
          } else {
            // Otherwise, let ReactFlow handle node type drops
            onDrop(event)
          }
        }}
        onDragOver={onDragOver}
      >
        <ReactFlow
          ref={ref}
          onInit={initializeViewport}
          defaultViewport={viewportSettings}
          viewport={viewportSettings}
          onlyRenderVisibleElements={false}
          onViewportChange={setViewportSettings}
          panOnScroll={panOnScroll}
          nodes={visibleNodes}
          edges={validEdges}
          onNodesChange={handleNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeMouseLeave={() => setPanOnScroll(false)}
          onNodeMouseEnter={() => setPanOnScroll(true)}
          onConnect={edgeHandlers.onConnect}
          onDragOver={onDragOver}
          onDrop={undefined} // Remove onDrop from ReactFlow to avoid duplicate handling
          onNodeDragStart={onNodeDragStart}
          onNodeDragStop={onNodeDragStop}
          onNodeClick={(event, node) => {
            onNodeClick(event, node)
            // Update side panel when a node is clicked
            selectNode(node.id)
          }}
          proOptions={proOptions}
          selectNodesOnDrag={true}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          connectionLineComponent={FloatingConnectionLine}
          deleteKeyCode="Delete"
          defaultEdgeOptions={defaultEdgeOptions}
          nodesDraggable={true}
          nodesConnectable={true}
          connectionMode={ConnectionMode.Loose}
          connectionRadius={20}
          elementsSelectable={true}
          snapToGrid={true}
          snapGrid={[32, 32]}
          selectionOnDrag={false}
          elevateNodesOnSelect={true}
          onNodeContextMenu={onNodeContextMenuWithCopy}
          onContextMenu={onPaneContextMenuWithPaste}
          onMove={onMove}
          onPaneClick={(event) => {
            onPaneClick(event)
            handlePaneOnClickAndCleanupActiveUsers(event)
            selectNode(null) // Deselect node when clicking background
            console.log('onClick')
          }}
          onPaneContextMenu={onPaneContextMenuWithPaste}
          onMouseMove={onMouseMove}
          minZoom={0.1}
          maxZoom={4}
          preventScrolling={true}
          zoomOnScroll={true}
          zoomOnPinch={true}
          panOnDrag={[0, 1, 2]}
          selectionMode={SelectionMode.Partial}
          selectionKeyCode="Shift"
          multiSelectionKeyCode="Control"
          colorMode={theme}
          {...edgeHandlers}
          onWheel={(event) => {
            const target = event.target as HTMLElement
            const isInScrollableContent = target.closest('.scrollable-content')
            if (isInScrollableContent) return
            const isOverNode = target.closest('.react-flow__node')
            if (isOverNode) {
              let scrollingElement: HTMLElement | null = null
              let currentElement: HTMLElement | null = target
              while (
                currentElement &&
                currentElement !== isOverNode &&
                !scrollingElement
              ) {
                const style = window.getComputedStyle(currentElement)
                const hasVerticalScroll =
                  (style.overflowY === 'auto' ||
                    style.overflowY === 'scroll' ||
                    style.overflowY === 'overlay') &&
                  currentElement.scrollHeight > currentElement.clientHeight
                if (hasVerticalScroll) scrollingElement = currentElement
                currentElement = currentElement.parentElement
              }
              if (scrollingElement) return
            }
          }}
          className="bg-gradient-to-r from-slate-200 to-slate-100 data-theme-dark:bg-gradient-to-r data-theme-dark:from-gray-900 data-theme-dark:to-gray-800"
        >
          <Cursors cursors={cursors} />
          <FlowControls
            setDebug={setDebug}
            newNode={newNode}
            setShowMap={setShowMap}
            onCreatingAnnotationNode={onCreatingAnnotationNode}
            createCustomNode={createCustomNode}
            createQuickStartNode={createQuickStartNode}
            onCreatingRandomSequenceNode={onCreatingRandomSequenceNode}
            createDocumentNode={createDocumentNode}
            createChatNode={createChatNode}
            createCompareFilesNode={createCompareFilesNode}
            createSmartNotesNode={createSmartNotesNode}
            createDocumentDesignerNode={createDocumentDesignerNode}
            createNodeIntellica={createNodeIntellica}
            createNodeDiagrams={createNodeDiagrams}
            createResearcherNode={createResearcherNode}
            createFileMinerNode={createFileMinerNode}
            createJavascriptNode={createJavascriptNode}
            createImageGeneratorNode={createImageGeneratorNode}
            createNodeResource={createNodeResource}
            applyAutoLayout={applyAutoLayout}
            applyHorizontalLayout={applyHorizontalLayout}
            applyVerticalLayout={applyVerticalLayout}
            applyGridLayout={applyGridLayout}
            applyOptimizedGridLayout={applyOptimizedGridLayout}
          />
          <Background
            color={currentBackgroundPreset.color}
            style={{ background: currentBackgroundPreset.background }}
            gap={currentBackgroundPreset.gap || 50}
            variant={
              currentBackgroundPreset.variant || BackgroundVariant.Cross
            }
            lineWidth={currentBackgroundPreset.lineWidth}
            size={currentBackgroundPreset.size}
            offset={currentBackgroundPreset.offset}
            patternClassName={currentBackgroundPreset.patternClassName}
          />
          {zoomLevel <= 0.1 && (
            <FlowFullMap
              setShowNodeIndex={setShowNodeIndex}
              handleNodeSelect={handleNodeSelect}
              projectName={projectName}
            />
          )}
          {/* Pass showMap to FlowMiniMap, but remove map button from inside FlowMiniMap */}
          {zoomLevel > 0.1 && showMap && (
            <FlowMiniMap
              showMap={showMap}
              setShowNodeIndex={setShowNodeIndex}
              handleNodeSelect={handleNodeSelect}
            />
          )}        </ReactFlow>
      </div>    </div>
  )
})

FlowCanvas.displayName = 'FlowCanvas'

export default FlowCanvas
